import React from 'react';

import classNames from 'classnames';

import { HeaderText, LuvImage } from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';

import { TypeBasicValuePropCardFields } from '@/core/generated-types';

const ICON_DESKTOP_INLINE_SIZES: Record<string, string> = {
  small: 'md:w-6',
  medium: 'md:w-8',
  large: 'md:w-10',
};

const ICON_MOBILE_INLINE_SIZES: Record<string, string> = {
  small: 'w-6',
  medium: 'w-8',
  large: 'w-[42px]',
};

const ICON_DESKTOP_STACKED_SIZES: Record<string, string> = {
  small: 'md:w-14',
  medium: 'md:w-16',
  large: 'md:w-[90px]',
};

const ICON_MOBILE_STACKED_SIZES: Record<string, string> = {
  small: 'w-12',
  medium: 'w-14',
  large: 'w-16',
};

export const BasicValuePropCard = (
  fields: TypeBasicValuePropCardFields & {
    desktopLayout: 'inline' | 'stacked';
    mobileLayout: 'inline' | 'stacked';
    desktopAlignment: 'center' | 'left';
    mobileAlignment: 'center' | 'left';
    iconSize: 'small' | 'medium' | 'large';
    headerSize: 'h3' | 'h4';
    classes?: string;
  },
) => {
  const {
    entryName,
    image,
    desktopAlignment,
    mobileAlignment,
    description,
    title,
    trailingTitle,
    classes,
    desktopLayout,
    mobileLayout,
    headerSize,
    iconSize,
  } = fields;

  const containerClasses = 'flex gap-2';

  const textAlignmentClasses = {
    center: 'md:text-center',
    left: 'md:text-left',
  };

  const titleClasses = classNames(
    'font-bradford font-medium',
    textAlignmentClasses,
  );

  const descriptionClasses = classNames(
    'text-sm font-normal leading-[22px]',
    textAlignmentClasses,
  );

  const desktopAlignmentClasses = {
    center: 'md:justify-start md:items-center',
    left: 'md:justify-start md:items-start',
  };

  const mobileAlignmentClasses = {
    center: 'justify-center items-start',
    left: `justify-start ${trailingTitle ? 'items-start' : 'items-center'}`,
  };

  const layoutClasses = {
    inline: 'md:flex-row',
    stacked: 'md:flex-col',
  };

  const mobileLayoutClasses = {
    inline: 'flex-row',
    stacked: 'flex-col',
  };

  const combinedClasses = classNames(
    containerClasses,
    classes,
    desktopAlignmentClasses[desktopAlignment],
    mobileAlignmentClasses[mobileAlignment],
    layoutClasses[desktopLayout],
    mobileLayoutClasses[mobileLayout],
  );

  const desktopMap =
    desktopLayout === 'inline'
      ? ICON_DESKTOP_INLINE_SIZES
      : ICON_DESKTOP_STACKED_SIZES;

  const mobileMap =
    mobileLayout === 'inline'
      ? ICON_MOBILE_INLINE_SIZES
      : ICON_MOBILE_STACKED_SIZES;

  const headerSizeOrFallback =
    headerSize === 'h4' ? HeaderSize.H4 : HeaderSize.H3;

  return (
    <div
      data-testid={`basic-value-prop-card-${entryName}`}
      className={classNames(combinedClasses)}
    >
      <figure
        className={classNames(
          `${mobileMap[iconSize]} ${desktopMap[iconSize]} shrink-0`,
        )}
      >
        <LuvImage imageData={image} alt={image?.fields?.altText} />
      </figure>
      <div>
        <HeaderText
          role="term"
          classes={titleClasses}
          text={title}
          size={headerSizeOrFallback}
        />
        <HeaderText
          role="term"
          classes={titleClasses}
          text={trailingTitle}
          size={headerSizeOrFallback}
        />
        {description && (
          <p role="definition" className={descriptionClasses}>
            {description}
          </p>
        )}
      </div>
    </div>
  );
};
